import React from 'react';
import { observer } from 'mobx-react';
import { useStore } from '../../../../store/store-provider';
import AttendeesTableHeader from '../../attendees-list-container/attendees-table/attendees-table-header/attendees-table-header';
import AttendeesTableRow from '../../attendees-list-container/attendees-table/attendees-table-row/attendees-table-row';
import { AttendeeEntity } from '../../../../store/attendees-store/attendee-entity/attendee-entity';
import NoAttendeeRow from './no-attendee-row/no-attendee-row';
import Paging from '../../../shared/paging/paging';
import { ATTENDEES_PER_PAGE } from '../../../../constans/common-constants';
import { getActivityAttendeeCountService } from '../../../../store/attendees-store/activity-attendee-count-service/activity-attendee-count-service-instance';
import LoaderSpinner from '../../../shared/custom-loader-spinner/loader-spinner';
import { AttendeeConnectionVariant, AttendeeStatus } from '../../../../constans/attendee-enums';
import { NO_DEPARTURE } from '../../../../constans/attendees-constants';
import { getFullDate } from '../../../../utils/helpers/date-helper';

const AttendeesTable = (): JSX.Element => {
  const { attendeesStore, permissionsStore, liveStreamStatusStore } = useStore();
  const { getAttendeesService, getPagingStore, getIsLoading, getAttendeesByFilter, getAttendeeConnectionControlService } = attendeesStore;
  const { isPodiumDisconnectEnabled } = permissionsStore;
  const { attendee } = getAttendeesService();
  const { onConnectionAttendeeAction } = getAttendeeConnectionControlService();
  const activeAttendeeNumber = getActivityAttendeeCountService().activeAttendeeNumber;
  const attendeeShowPagingCondition = activeAttendeeNumber <= ATTENDEES_PER_PAGE ? 'no-page' : '';

  const onSelectPageCallback = (): void => {
    getAttendeesByFilter();
  };

  const shouldDisableAttendeeConnectionButton = (attendee: AttendeeEntity): boolean => {
    return attendee.getViewerId().includes('[UNKNOWN_');
  };

  const shouldHideAttendeeConnectionButton = (attendee: AttendeeEntity): boolean => {
    return attendee.getAttendeeStatus() === AttendeeStatus.DEPARTED
      || attendee.getAttendeeStatus() === AttendeeStatus.DISCONNECTING;
  };

  const shouldRenderAttendeeConnectionButton = (): boolean => {
    return !!isPodiumDisconnectEnabled() && !!liveStreamStatusStore.getLivePresentationId();
  };

  const shouldRenderAttendeeStatusLabel = (attendee: AttendeeEntity): boolean => {
    return attendee.getAttendeeStatus() === AttendeeStatus.DISCONNECTING;
  };

  const attendeeStatusLabel = (attendee: AttendeeEntity): string | undefined => {
    const map: { [key in string]: string } = {
      [AttendeeStatus.DISCONNECTING]: 'Disconnecting...'
    };

    return map[attendee.getAttendeeStatus()];
  };

  const attendeeConnectionButtonLabel = (attendee: AttendeeEntity): string => {
    const status = attendee.getAttendeeStatus();

    if (status === AttendeeStatus.DISCONNECTED) {
      return AttendeeConnectionVariant.RECONNECT;
    }

    if (status === AttendeeStatus.ACTIVE) {
      return AttendeeConnectionVariant.DISCONNECT;
    }

    return '';
  };

  const prepareAttendeeInfo = (attendee: AttendeeEntity) => {
    const departure = attendee.getAttendeeStatus() === AttendeeStatus.DISCONNECTING
    || attendee.attendeeStatus === AttendeeStatus.DISCONNECTED
    || attendee.getAttendeeInfo().departure === '0'
      ? NO_DEPARTURE
      : getFullDate(attendee.getAttendeeInfo().departure);

    return {
      ...attendee.getAttendeeInfo(),
      departure: departure
    };
  };

  return (
    <div className={'table ' + attendeeShowPagingCondition}>
      <AttendeesTableHeader/>
      <div className='table-body'>
        <div className='table-body__content'>
          {getIsLoading() ?
            <LoaderSpinner customClasses='spinner-big spinner-blue'/> :
            attendee.length
              ? attendee.map((attendeeEntity: AttendeeEntity) => (
                <AttendeesTableRow
                  {...prepareAttendeeInfo(attendeeEntity)}
                  disableAttendeeConnectionButton={shouldDisableAttendeeConnectionButton(attendeeEntity)}
                  hideAttendeeConnectionButton={shouldHideAttendeeConnectionButton(attendeeEntity)}
                  renderAttendeeConnectionButton={shouldRenderAttendeeConnectionButton()}
                  onConnectionAttendeeAction={onConnectionAttendeeAction.bind(null, attendeeEntity)}
                  shouldRenderAttendeeStatusLabel={shouldRenderAttendeeStatusLabel(attendeeEntity)}
                  attendeeStatusLabel={attendeeStatusLabel(attendeeEntity)}
                  attendeeConnectionButtonLabel={attendeeConnectionButtonLabel(attendeeEntity)}
                  key={attendeeEntity.getViewerId()}
                />
              ))
              : <NoAttendeeRow/>
          }
        </div>
        {!getIsLoading() && <Paging pagingStore={getPagingStore()} onSelectPageCallback={onSelectPageCallback}/>}
      </div>
    </div>
  );
};

export default observer(AttendeesTable);